/**
 * API client functions for the Deployment Trigger Configuration.
 * Handles retrieving and saving per-repository deployment trigger settings.
 */

import { apiClient } from '@/modules/api/api-client';

// --- Types ---

export interface DeployTriggerConfig {
  enabled: boolean;
  targetProjectKey: string;
  targetEnvironment: string;
  artifactIdPattern: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface DeployTriggerConfigRequest {
  enabled: boolean;
  targetProjectKey: string;
  targetEnvironment: string;
  artifactIdPattern: string;
}

export interface ValidationErrorResponse {
  message: string;
  fieldErrors?: Record<string, string>;
}

// --- Deploy Trigger Config APIs ---

/**
 * Retrieves the deployment trigger configuration for a repository.
 * Returns the config if it exists, or throws an ApiError with status 404 if not configured.
 */
export async function getDeployTriggerConfig(repoName: string): Promise<DeployTriggerConfig> {
  return apiClient.get<DeployTriggerConfig>(
    `/repos/${encodeURIComponent(repoName)}/ci/deploy-trigger`
  );
}

/**
 * Creates or updates the deployment trigger configuration for a repository.
 * Returns the saved configuration on success.
 * Throws ApiError with status 400 and field-level validation errors on invalid input.
 */
export async function saveDeployTriggerConfig(
  repoName: string,
  config: DeployTriggerConfigRequest
): Promise<DeployTriggerConfig> {
  return apiClient.put<DeployTriggerConfig>(
    `/repos/${encodeURIComponent(repoName)}/ci/deploy-trigger`,
    config,
    { timeout: 30_000 }
  );
}
